import { defineStore } from 'pinia';
import {
    type UseColorModeReturn,
    eagerComputed,
    useColorMode,
} from '@vueuse/core';
import { type ColorScheme, colorSchemeClasses } from './color-schemes';
import { computed, reactive } from 'vue';
import {
    setStylesheetLink,
    deleteStylesheet,
    setStylesheetTag,
} from '@precon-tools-suite-by-shared/browser-utils';

export type StylesheetReference = string | (() => Promise<{ default: string }>);
export type StylesheetReferences = Partial<
    Record<ColorScheme, StylesheetReference | null>
>;

export const useColorSchemeStylesheetStore = defineStore(
    'color-scheme-stylesheets',
    () => {
        const stylesheets = reactive(new Map<string, StylesheetReferences>());

        const keys = computed(() => [...stylesheets.keys()]);
        const scheme = useColorMode({
            modes: colorSchemeClasses,
            onChanged: () => {
                for (const sheetId of stylesheets.keys()) update(sheetId);
            },
        }) as UseColorModeReturn<ColorScheme>;

        const stylesheetLoading = reactive(new Map<string, boolean>());

        function getReference(id: string): StylesheetReference | null {
            const references = stylesheets.get(id);
            if (!references) return null;
            return references[scheme.value] ?? null;
        }

        function getLoading(id: string): boolean {
            return stylesheetLoading.get(id) ?? false;
        }

        const loading = eagerComputed(() => {
            for (const stylesheetId of stylesheets.keys()) {
                if (getLoading(stylesheetId)) {
                    return true;
                }
            }
            return false;
        });

        async function update(id: string) {
            stylesheetLoading.set(id, true);
            try {
                const reference = getReference(id);
                if (typeof reference === 'string') {
                    await setStylesheetLink(id, reference);
                } else if (reference) {
                    const content = (await reference()).default;
                    setStylesheetTag(id, content);
                } else {
                    deleteStylesheet(id);
                }
            } finally {
                stylesheetLoading.set(id, false);
            }
        }

        async function set(id: string, references: StylesheetReferences) {
            stylesheets.set(id, references);
            return update(id);
        }

        function has(id: string) {
            return stylesheets.has(id);
        }

        function deleteSheet(id: string) {
            stylesheets.delete(id);
            stylesheetLoading.delete(id);
            deleteStylesheet(id);
        }

        return {
            getLoading,
            set,
            has,
            keys,
            delete: deleteSheet,
            loading,
        };
    }
);
